import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface ContextData {
  pros_docs: unknown[];
  current_applications: unknown[];
  current_patents: unknown[];
  current_publications: unknown[];
  resource_ids: string[];
  resource_names: string[];
  mpep_sections: unknown[];
  usc_sections: unknown[];
  fed_reg_docs: unknown[];
  last_context_number: string | null;
  last_context_type: string | null;
}

interface ContextState {
  contextData: ContextData;
  patentResources: unknown | null;
  removedResources: unknown[];
  fullApplicationCache: Record<string, unknown>;

  setContextData: (data: ContextData | ((prev: ContextData) => ContextData)) => void;
  setPatentResources: (resources: unknown | null) => void;
  setRemovedResources: (resources: unknown[]) => void;
  addRemovedResource: (resource: unknown) => void;
  setFullApplicationCache: (cache: Record<string, unknown>) => void;
  updateFullApplicationCache: (appNumber: string, data: unknown) => void;

  resetContext: () => void;
}

const initialContextData: ContextData = {
  pros_docs: [],
  current_applications: [],
  current_patents: [],
  current_publications: [],
  resource_ids: [],
  resource_names: [],
  mpep_sections: [],
  usc_sections: [],
  fed_reg_docs: [],
  last_context_number: null,
  last_context_type: null,
};

export const useContextStore = create<ContextState>()(
  devtools((set) => ({
    contextData: initialContextData,
    patentResources: null,
    removedResources: [],
    fullApplicationCache: {},

    setContextData: (data) => set((state) => ({
      contextData: typeof data === 'function' ? data(state.contextData) : data
    })),

    setPatentResources: (resources) => set({ patentResources: resources }),

    setRemovedResources: (resources) => set({ removedResources: resources }),

    addRemovedResource: (resource) => set((state) => ({
      removedResources: [...state.removedResources, resource]
    })),

    setFullApplicationCache: (cache) => set({ fullApplicationCache: cache }),

    updateFullApplicationCache: (appNumber, data) => set((state) => ({
      fullApplicationCache: {
        ...state.fullApplicationCache,
        [appNumber]: data
      }
    })),

    resetContext: () => set({
      contextData: initialContextData,
      removedResources: [],
    }),
  }))
);
